import { useQueries, useQuery } from '@tanstack/react-query';
import {
  BillingKeys,
  getBillingInfoModelQuery,
  getHubSpotCompanyQuery,
  getPendingOrdersForType,
  getProductAssociations,
  getStripeKeyDetails,
  getSubscriptions,
} from '../../api/billing-api';
import { useUserSettings } from '../../utils/use-user-settings';
import { Messages } from '../../utils/message-constants';
import { useParams } from 'react-router-dom';
import BillingFormPage from './billing-form-page';
import { StripeProvider } from './payment-methods/stripe-provider';
import FormSkeleton from './form-skeleton';
import SimpleBreadcrumbsPage from '../../shared/simple-breadcrumbs-page';
import { ModuleList } from '../../shared/analytics/module-list';
import { pagesConfig } from '../../shared/pages-config';
import Card from '@mui/material/Card';

export default function AddLicenseForm() {
  const { id } = useParams();
  const selectedRowId = id ? Number(id) : '0';
  const { qbInstance } = useUserSettings();

  const { isLoading, data } = useQuery({
    queryKey: [BillingKeys.ProductAssociations],
    queryFn: () => getProductAssociations(),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('product associations'),
    },
  });

  const { isLoading: isLoadingSubs, data: subscriptionsRecord } = useQuery({
    queryKey: [BillingKeys.Subscriptions],
    queryFn: () => getSubscriptions(data!),
    enabled: !!data,
    meta: {
      errorMessage: Messages.Errors.Network.Loading('subscriptions'),
    },
  });

  const subscriptions = subscriptionsRecord?.subscriptions ?? [];
  const bundledGpanelSubscriptions = subscriptionsRecord?.gPanelSubscriptions;
  const isGoogleArchivedUsers = subscriptionsRecord?.isGoogleArchivedUser;
  const bundledGpanelSubscription = bundledGpanelSubscriptions
    ? bundledGpanelSubscriptions[selectedRowId]
    : null;
  const isGoogleArchived = isGoogleArchivedUsers
    ? isGoogleArchivedUsers[selectedRowId]
    : false;

  const subscription = subscriptions?.[selectedRowId];

  const { isLoading: isLoadingBillingInfo, data: billingInformationModel } =
    useQuery(getBillingInfoModelQuery());

  const { isLoading: isLoadingHubSpotCompany, data: hubSpotCompany } = useQuery(
    getHubSpotCompanyQuery()
  );

  const { data: stripeKeyDetails } = useQuery({
    queryKey: [BillingKeys.StripeKeyType, qbInstance],
    queryFn: () => getStripeKeyDetails(qbInstance),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('stripe information'),
    },
  });

  const results = useQueries({
    queries: subscriptions
      ? subscriptions.map((sub) => {
          return {
            queryKey: [BillingKeys.PendingOrders, sub.productModel],
            queryFn: () => getPendingOrdersForType(sub.productModel.product),
          };
        })
      : [],
  });

  const subscriptionOrders = results
    .map((queryResult) => queryResult.data)
    .flat()
    .filter(
      (purchaseOrder) =>
        purchaseOrder?.licType === subscription.productModel.productName
    );

  if (
    !hubSpotCompany ||
    !subscription ||
    !stripeKeyDetails ||
    !billingInformationModel ||
    isLoadingBillingInfo ||
    isLoadingHubSpotCompany ||
    isLoadingSubs ||
    isLoading
  )
    return (
      <SimpleBreadcrumbsPage
        isSubPage
        breadcrumbs={[
          { label: ModuleList.modules.billing.name },
          {
            label: pagesConfig.billingSubscriptions.name,
            path: `${pagesConfig.billing.link}/${pagesConfig.billingSubscriptions.link}`,
          },
        ]}
      >
        <Card sx={{ width: '100%', height: '100%', overflow: 'hidden' }}>
          <FormSkeleton />
        </Card>
      </SimpleBreadcrumbsPage>
    );

  return (
    <SimpleBreadcrumbsPage
      isSubPage
      breadcrumbs={[
        { label: ModuleList.modules.billing.name },
        {
          label: pagesConfig.billingSubscriptions.name,
          path: `${pagesConfig.billing.link}/${pagesConfig.billingSubscriptions.link}`,
        },
      ]}
    >
      <StripeProvider stripePublicKey={stripeKeyDetails.publicKey}>
        <BillingFormPage
          subscription={subscription}
          hubSpotCompany={hubSpotCompany}
          billingInfoModel={billingInformationModel}
          pendingPurchaseOrders={subscriptionOrders}
          totalPurchasedWorkspaceLicenses={
            subscriptionsRecord?.totalPurchasedWorkspaceLicenses ?? 0
          }
          totalPurchasedAULicenses={
            subscriptionsRecord?.totalPurchasedAULicenses ?? 0
          }
          bundledGpanelSubscription={bundledGpanelSubscription}
          isGoogleArchived={isGoogleArchived}
        />
      </StripeProvider>
    </SimpleBreadcrumbsPage>
  );
}
